import { Feature, Point as GeoJSONPoint, Geometry, Position } from 'geojson';
import flow from 'lodash/flow';
import {
  StopRegistryGeoJson,
  StopRegistryGeoJsonType,
} from '../generated/graphql';
import { NonNullableKeys, Point } from '../types';

export function mapPointToGeoJSON({
  longitude,
  latitude,
  elevation = 0,
}: Point) {
  const geoJsonPoint: GeoJSONPoint = {
    type: 'Point',
    coordinates: [longitude, latitude, elevation],
  };
  return geoJsonPoint;
}

type StopRegistryGeoJsonDefined = {
  coordinates: Position;
  type: StopRegistryGeoJsonType;
};

export function mapPointToStopRegistryGeoJSON({
  longitude,
  latitude,
}: Point): StopRegistryGeoJsonDefined {
  return {
    type: StopRegistryGeoJsonType.Point,
    coordinates: [
      longitude,
      latitude,
      /* Tiamat does not support altitude, and currently breaks quite badly if one attempts to persist it. */
    ],
  };
}

export function mapLngLatToPoint(
  lngLat: ReadonlyArray<number>,
  maxPrecision: number = 10,
): Point {
  if (lngLat.length < 2 || lngLat.length > 3) {
    throw new Error(
      `Expected lngLat to be like [number, number] or [number, number, number] but got ${lngLat}`,
    );
  }

  const [lon, lat, ele = 0] = lngLat;

  return {
    longitude: maxPrecision ? Number(lon.toPrecision(maxPrecision)) : lon,
    latitude: maxPrecision ? Number(lat.toPrecision(maxPrecision)) : lat,
    elevation: maxPrecision ? Number(ele.toPrecision(maxPrecision)) : ele,
  };
}

export const mapLngLatToGeoJSON = flow(mapLngLatToPoint, mapPointToGeoJSON);

export function mapGeoJSONtoFeature<T extends Geometry>(geoJson: T) {
  const feature: Feature<T> = {
    type: 'Feature',
    geometry: geoJson,
    properties: {},
  };
  return feature;
}

export type ValidGeoJsonPoint = Required<
  NonNullableKeys<StopRegistryGeoJson, 'coordinates'>
> & { type: StopRegistryGeoJsonType.Point };

export function isValidGeoJSONPoint(
  geometry: Geometry | StopRegistryGeoJson | null | undefined,
): geometry is ValidGeoJsonPoint {
  return (
    !!geometry && geometry.type === 'Point' && !!geometry.coordinates?.length
  );
}

export function getGeometryPoint(geometry: undefined): null;
export function getGeometryPoint(geometry: null): null;
export function getGeometryPoint(geometry: GeoJSONPoint): Point;
export function getGeometryPoint(
  geometry: Geometry | StopRegistryGeoJson | null | undefined,
): Point | null;
export function getGeometryPoint(
  geometry: Geometry | StopRegistryGeoJson | null | undefined,
): Point | null {
  if (isValidGeoJSONPoint(geometry)) {
    return mapLngLatToPoint(geometry.coordinates);
  }

  return null;
}

export function getPointPosition(
  geometry: Geometry | StopRegistryGeoJson | null | undefined,
): Position | null {
  if (isValidGeoJSONPoint(geometry)) {
    return geometry.coordinates;
  }

  return null;
}
